import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { DictItemRow, DictRow, HqApi } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Col, Form, Input, InputNumber, Modal, Row, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';

/** 数据字典：平台字典（tenant_id=0）+ 本院字典，前端枚举的兜底来源。 */
export function SystemDictPage({ hq, canManage }: { hq: HqApi; canManage: boolean }) {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [keyword, setKeyword] = useState('');
  const [active, setActive] = useState<DictRow | null>(null);
  const [dictOpen, setDictOpen] = useState(false);
  const [itemEditing, setItemEditing] = useState<DictItemRow | null>(null);
  const [itemOpen, setItemOpen] = useState(false);
  const [dictForm] = Form.useForm();
  const [itemForm] = Form.useForm();

  const dicts = useQuery({ queryKey: ['system', 'dict', keyword], queryFn: () => hq.opsExtras.dict.list(keyword) });
  const items = useQuery({
    queryKey: ['system', 'dict', 'items', active?.dictCode],
    enabled: Boolean(active),
    queryFn: () => hq.opsExtras.dict.items(active?.dictCode ?? '')
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['system', 'dict'] });
  };

  const saveDict = useMutation({
    mutationFn: () => hq.opsExtras.dict.save(dictForm.getFieldsValue()),
    onSuccess: async () => {
      message.success('字典已保存');
      setDictOpen(false);
      await refresh();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const saveItem = useMutation({
    mutationFn: () =>
      hq.opsExtras.dict.saveItem({
        ...itemForm.getFieldsValue(),
        dictCode: active?.dictCode
      }),
    onSuccess: async () => {
      message.success('字典项已保存');
      setItemOpen(false);
      await refresh();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const removeItem = (row: DictItemRow) => {
    modal.confirm({
      title: `删除 ${row.itemLabel}`,
      content: '删除后前端枚举将回落到代码内置值。',
      okButtonProps: { danger: true },
      onOk: async () => {
        await hq.opsExtras.dict.removeItem(row.id);
        message.success('已删除');
        await refresh();
      },
      onCancel: () => undefined
    });
  };

  const dictColumns: ColumnsType<DictRow> = [
    { title: '字典编码', dataIndex: 'dictCode', width: 180 },
    { title: '名称', dataIndex: 'dictName', width: 220 },
    { title: '来源', dataIndex: 'tenantId', width: 90, render: (value: string) => (value === '0' ? <Tag color="blue">平台</Tag> : <Tag>本院</Tag>) },
    { title: '项数', dataIndex: 'itemCount', width: 80, align: 'right' },
    { title: '更新时间', dataIndex: 'updateAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<DictItemRow> = [
    { title: '值', dataIndex: 'itemValue', width: 160 },
    { title: '显示名', dataIndex: 'itemLabel', width: 200 },
    { title: '英文名', dataIndex: 'itemLabelEn', width: 140, render: (value: string | null) => value || '-' },
    { title: '上级值', dataIndex: 'parentValue', width: 120, render: (value: string | null) => value || '-' },
    { title: '排序', dataIndex: 'sortNo', width: 80, align: 'right' },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      title: '操作',
      width: 140,
      render: (_, row) => (
        <Space size={0}>
          <Button
            type="link"
            disabled={!canManage}
            onClick={() => {
              setItemEditing(row);
              itemForm.setFieldsValue(row);
              setItemOpen(true);
            }}
          >
            编辑
          </Button>
          <Button type="link" danger disabled={!canManage} onClick={() => removeItem(row)}>
            删除
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={<Space>数据字典<Typography.Text type="secondary">平台字典只读；本院字典可维护（sys:config:manage）</Typography.Text></Space>}
      extra={
        <Space>
          <Input.Search allowClear placeholder="编码 / 名称" style={{ width: 220 }} onSearch={(value) => setKeyword(value.trim())} />
          <Button icon={<ReloadOutlined />} onClick={() => refresh()} loading={dicts.isFetching}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canManage}
            onClick={() => {
              dictForm.resetFields();
              setDictOpen(true);
            }}
          >
            新增本院字典
          </Button>
        </Space>
      }
    >
      {!canManage ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message="当前账号只读：维护字典需要 sys:config:manage。" /> : null}
      <Row gutter={16}>
        <Col span={10}>
          <Table<DictRow>
            rowKey="id"
            size="middle"
            columns={dictColumns}
            dataSource={dicts.data ?? []}
            loading={dicts.isPending}
            pagination={false}
            onRow={(row) => ({ onClick: () => setActive(row), style: { cursor: 'pointer' } })}
            rowClassName={(row) => (row.id === active?.id ? 'ant-table-row-selected' : '')}
            scroll={{ y: 520 }}
          />
        </Col>
        <Col span={14}>
          <Space style={{ marginBottom: 12 }}>
            <Typography.Text strong>{active ? `${active.dictName}（${active.dictCode}）` : '选择左侧字典查看项'}</Typography.Text>
            {active ? (
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                disabled={!canManage}
                onClick={() => {
                  setItemEditing(null);
                  itemForm.resetFields();
                  itemForm.setFieldsValue({ status: 'ACTIVE', sortNo: 0 });
                  setItemOpen(true);
                }}
              >
                新增项
              </Button>
            ) : null}
          </Space>
          <Table<DictItemRow>
            rowKey="id"
            size="middle"
            columns={itemColumns}
            dataSource={items.data ?? []}
            // items 未选中字典时是 disabled 查询，react-query 仍把它算作 pending —— 直接绑 isPending 会永远转圈
            loading={Boolean(active) && items.isPending}
            pagination={{ pageSize: 12, hideOnSinglePage: true }}
            scroll={{ x: 900 }}
          />
        </Col>
      </Row>

      <Modal
        title="新增本院字典"
        open={dictOpen}
        onCancel={() => setDictOpen(false)}
        onOk={() => dictForm.submit()}
        confirmLoading={saveDict.isPending}
        destroyOnHidden
      >
        <Form form={dictForm} layout="vertical" onFinish={() => saveDict.mutate()} preserve={false}>
          <Form.Item name="dictCode" label="字典编码" rules={[{ required: true }]}>
            <Input maxLength={64} placeholder="如 WL_MATERIAL_ABC" />
          </Form.Item>
          <Form.Item name="dictName" label="字典名称" rules={[{ required: true }]}>
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={itemEditing ? '编辑字典项' : '新增字典项'}
        open={itemOpen}
        onCancel={() => setItemOpen(false)}
        onOk={() => itemForm.submit()}
        confirmLoading={saveItem.isPending}
        destroyOnHidden
      >
        <Form form={itemForm} layout="vertical" onFinish={() => saveItem.mutate()} preserve={false}>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="itemValue" label="值" rules={[{ required: true }]} style={{ width: 220 }}>
              <Input maxLength={64} disabled={Boolean(itemEditing)} />
            </Form.Item>
            <Form.Item name="itemLabel" label="显示名" rules={[{ required: true }]} style={{ width: 280 }}>
              <Input maxLength={128} />
            </Form.Item>
          </Space>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="itemLabelEn" label="英文名" style={{ width: 220 }}>
              <Input maxLength={128} />
            </Form.Item>
            <Form.Item name="parentValue" label="上级值" style={{ width: 220 }}>
              {/* 上级必须是本字典已有项，手填拼错会造成孤立项 */}
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="无上级"
                options={(items.data ?? [])
                  .filter((item) => item.itemValue !== itemEditing?.itemValue)
                  .map((item) => ({ value: item.itemValue, label: `${item.itemValue} · ${item.itemLabel}` }))}
              />
            </Form.Item>
            <Form.Item name="sortNo" label="排序" style={{ width: 100 }}>
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          {/* 后端只接受 ACTIVE/INACTIVE（DictAdminService.Statuses），手填其他值会被拒 */}
          <Form.Item name="status" label="状态" initialValue="ACTIVE" style={{ width: 180 }}>
            <Select options={[{ value: 'ACTIVE', label: '启用 ACTIVE' }, { value: 'INACTIVE', label: '停用 INACTIVE' }]} />
          </Form.Item>
          <Form.Item name="extendJson" label="扩展 JSON">
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
